import React, { useContext, useEffect, useState } from 'react';
import SectionLayout from '../layouts/SectionLayout/index';
import { useDialog } from '../Dialog/index';
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from '@src/context/GlobalContextProvider';
import { GTM } from '@src/analytics/gtm';
import { gtmEvents } from '@src/analytics/gtmEventsConstant';
import { useCustomToast } from '@components/extra/useCustomToast';
import { ELeadSquaredActivityCode } from '@src/constants/leadsquaredActivityConstants/index';
import { EButtonType } from '@src/constants/data/programs/index';
import { useInView } from 'react-intersection-observer';
import useDataScienceProgram from '@src/hooks/useDataScienceProgram';
import { cn } from '@/lib/utils';
import DesktopCurriculum from './DesktopCurriculum';
import MobileCurriculum from './MobileCurriculum';
import { EProgramTypeId } from '@src/constants/campaign';
import { useLocation } from '@reach/router';
import { getProgramTypeFromPath } from '@src/constants/pages';
import useCurriculumDownload from '@src/hooks/useCurriculumDownload';
import useSendActivities from '@src/hooks/useSendActivities';
import LeadGenerationButton from '@components/LeadGenerationForm/LeadGenerationButton';
import { LEAD_GEN_DOWNLOAD_CURRICULUM } from '@src/constants/LeadGenerationConstants/index';
const CurriculumV4 = React.memo(function CurriculumV4({
  dark,
  type,
  curriculum,
  selectedProgram,
  dreamCompanies,
  superDreamCompanies,
  advancedCurriculum,
  headingClassName,
  isTypeQA,
  isTypeFellowShipPlus,
  advancedCurriculumHeading,
  hideHeader,
  hideAdvanceCurriculumSection,
  isSDPage,
  brochureViewHref,
  ...props
}) {
  const { downloadCurriculum } = useCurriculumDownload();
  const [openDialog, closeDialog] = useDialog();
  const [toggle, setToggle] = useState('Premium');
  const [isAdvancedCurriculum, setIsAdvancedCurriculum] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;

  const isAcceleratePage =
    location?.pathname === '/provisional-enrollment/accelerate/';

  const programType = getProgramTypeFromPath(pathname);

  const { isDataSciencePage, isDataScienceProvisionalPage } =
    useDataScienceProgram();

  const currentCurriculum =
    toggle === 'Premium' || selectedProgram?.id === EProgramTypeId.DA
      ? curriculum
      : advancedCurriculum;

  const { sendActivityHandler } = useSendActivities();

  //   const location = useLocation();
  const dispatch = useContext(GlobalDispatchContext);
  const state = useContext(GlobalStateContext);
  const downloadCurriculumType = isTypeFellowShipPlus
    ? 'FELLOWSHIP_PLUS'
    : isTypeQA
    ? 'QA_AUTOMATION_TESTING'
    : isDataSciencePage
    ? 'DATA_ANALYTICS'
    : 'FELLOWSHIP';

  const triggerSuccessToast = useCustomToast(
    'success',
    <div className="">
      <h2 className="font-base font-rubik text-lg">Success!</h2>
      <p className="font-rubik text-xs">
        Your curiculum is downloaded successfully.
      </p>
    </div>,
  );

  //Monitoring elements within the viewport to record them for WebEngage events.
  //   const isMobile = useResizer();

  const [curriculumSection1Ref, inViewCurriculumSection1] = useInView({
    triggerOnce: true,
    threshold: 1.0,
  });
  const [curriculumSection2Ref, inViewCurriculumSection2] = useInView({
    triggerOnce: true,
    threshold: 1.0,
  });
  const [curriculumSection3Ref, inViewCurriculumSection3] = useInView({
    triggerOnce: true,
    threshold: 1.0,
  });

  // Consolidating into a single useEffect is avoided due to the concern
  // of pushing multiple instances of the same event into the dataLayer.

  useEffect(() => {
    if (inViewCurriculumSection1) {
      GTM.track(gtmEvents.SCROLL_TO_CURRICULUM_SECTION, {
        type: type,
        location: 'CURRICULLUM_SECTION_1',
      });
    }
  }, [inViewCurriculumSection1]);

  useEffect(() => {
    if (inViewCurriculumSection2) {
      GTM.track(gtmEvents.SCROLL_TO_CURRICULUM_SECTION, {
        type: type,
        location: 'CURRICULLUM_SECTION_2',
      });
    }
  }, [inViewCurriculumSection2]);

  useEffect(() => {
    if (inViewCurriculumSection3) {
      GTM.track(gtmEvents.SCROLL_TO_CURRICULUM_SECTION, {
        type: type,
        location: 'CURRICULLUM_SECTION_3',
      });
    }
  }, [inViewCurriculumSection3]);

  const triggerCurriculumWebengageFlow = (email, phone, type) => {
    GTM.login({
      userId: email,
      email: email,
      phone: phone,
      whatsappNumber: phone,
    });
    GTM.setEmail(email);

    if (phone) {
      GTM.track(gtmEvents.PHONE_SUBMITTED, {
        phone: phone,
        type: 'DOWNLOAD_CURRICULUM_STRIP',
      });
    }

    window.webengage &&
      window.webengage.user.setAttribute('we_whatsapp_opt_in', true);

    //GTM Event for Email Capture
    if (!state.isEmailEventSubmitted) {
      GTM.track(gtmEvents.EMAIL_SUBMITTED, {
        type: downloadCurriculumType,
        location: 'DOWNLOAD_CURRICULUM_STRIP',
      });
      dispatch({
        type: 'SET_IS_EMAIL_EVENT_SUBMITTED',
        payload: true,
      });
    }

    GTM.track(gtmEvents.DOWNLOAD_CURRICULUM_APPLIED_SUCCESSFULLY, {
      type: downloadCurriculumType,
      location: 'STRIP',
    });

    if (type == EButtonType.DOWNLOAD_CURRICULUM_BUTTON) {
      sendActivityHandler(
        ELeadSquaredActivityCode.SD_PAGE_DOWNLOAD_CURRICULUM_CLICKED,
      );
    } else {
      sendActivityHandler(
        ELeadSquaredActivityCode.SD_PAGE_DOWNLOAD_CURRICULUM_SUBMITTED,
      );
    }

    // New flow: after the user gives email+phone we route them to the
    // per-program /view-brochure page (if the YAML has a brochurePdf set)
    // instead of directly downloading the old handbook PDF. We fall back
    // to the legacy download behaviour when no brochureViewHref is
    // provided so pages without a brochure keep working unchanged.
    if (brochureViewHref) {
      try {
        GTM.track(gtmEvents.BROCHURE_VIEW_CTA_CLICKED, {
          type: type,
          location: 'CURRICULUM_SECTION',
          href: brochureViewHref,
        });
      } catch (err) {
        // analytics must never block navigation
      }
      window.open(brochureViewHref, '_blank', 'noopener,noreferrer');
    } else {
      downloadCurriculum();
    }
    triggerSuccessToast();
  };

  let firstCurriculum = [],
    optionCurriculum = [],
    secondCurriculum = [];

  for (let i = 0; i < curriculum?.length; i++) {
    if (curriculum[i]?.tag?.includes('OPTION_')) {
      optionCurriculum.push(curriculum[i]);
      continue;
    }
    if (optionCurriculum.length == 0) {
      firstCurriculum.push(curriculum[i]);
    } else {
      secondCurriculum.push(curriculum[i]);
    }
  }

  // const curriculumSection = isTypeQA ? curriculum : firstCurriculum;

  const dreamJobRange = isDataSciencePage
    ? '6 To 15 LPA'
    : props.dreamJobRange || '6 To 15 LPA';
  const superDreamJobRange = isAdvancedCurriculum
    ? programType === 'QA'
      ? '8 To 20 LPA'
      : props.superDreamJobRange || '15 To 40 LPA'
    : '15 To 40 LPA';

  return (
    <SectionLayout
      headingClassName={headingClassName}
      heading={''}
      description={''}
      className="md:py-0"
      {...props}
    >
      <div className="container ">
        {!props.hideCurriculumSection && (
          <div
            className={cn(
              'rounded-[40px] p-4  sm:p-6  md:rounded-[50px] ',
              `${
                !dark
                  ? 'bg-[#E6F4F2] text-v5-neutral-600'
                  : 'bg-v5-green-500 text-white'
              }`,
              //   `${hideAdvanceCurriculumSection ? '' : 'mb-20'}`,
            )}
          >
            {isAdvancedCurriculum &&
            (programType === 'FSD' || programType === 'QA') ? (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Additional Advanced Curriculum to Crack{' '}
                <br className="hidden sm:block"></br>
                Super-Dream Jobs ({superDreamJobRange})
                <br className="hidden sm:block"></br> At Unicorns And Tech
                Giants
              </h1>
            ) : programType === 'SKILLQ_FULL_STACK_DEVELOPMENT_PROGRAM' ? (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Crio SkillQ <br className="hidden sm:block"></br> Gen-AI Powered
                Full Stack Program
              </h1>
            ) : programType === 'SKILLQ_DATA_ANALYTICS_PROGRAM' ? (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Crio SkillQ <br className="hidden sm:block"></br> Gen-AI Powered
                Data Analytics & Data Science Program
              </h1>
            ) : (isDataSciencePage || isDataScienceProvisionalPage) &&
              isAdvancedCurriculum ? (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Curriculum to crack Super dream jobs
                <br className="hidden sm:block"></br>
                (6 - 15 LPA) in top product-based{' '}
                <br className="hidden sm:block"></br>Companies
              </h1>
            ) : isDataSciencePage || isDataScienceProvisionalPage ? (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Curriculum to crack dream jobs
                <br className="hidden sm:block"></br>
                (6 - 10 LPA) in top product-based{' '}
                <br className="hidden sm:block"></br>Companies
              </h1>
            ) : (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Curriculum to crack dream jobs{' '}
                <br className="hidden sm:block"></br>({dreamJobRange}) in top
                Product-Based <br className="hidden sm:block"></br>companies
              </h1>
            )}
            {advancedCurriculum ? (
              <div className="mb-5 flex items-center justify-center">
                <div
                  className={cn(
                    'flex h-[45px] w-[280px] rounded-[8px] p-2 md:h-[72px] md:w-[450px] md:rounded-[10px]',
                    `${dark ? 'bg-v5-green-700' : 'bg-white'}`,
                  )}
                >
                  {['Premium', 'Advanced'].map((menu) => (
                    <div
                      key={menu}
                      className={cn(
                        'flex h-full flex-1 cursor-pointer flex-col items-center justify-center rounded-[8px] md:w-[210px] md:rounded-[10px]',
                        `${
                          toggle === menu
                            ? dark
                              ? 'bg-v5-green-400'
                              : 'bg-v5-green-100'
                            : ''
                        }`,
                      )}
                      onClick={() => {
                        setIsAdvancedCurriculum(menu === 'Advanced');
                        setToggle(menu);
                      }}
                    >
                      <h3 className="text-[14px] font-semibold md:text-[20px]">
                        {isAcceleratePage
                          ? menu === 'Premium'
                            ? 'MERN Developer'
                            : 'JAVA Developer'
                          : menu}
                      </h3>
                      {menu === 'Premium' && isAcceleratePage && (
                        <i className="text-[6px] md:text-[9px]">6 Months</i>
                      )}
                      {menu === 'Advanced' && isAcceleratePage && (
                        <i className="text-[6px] md:text-[9px]">6 Months</i>
                      )}
                      {menu === 'Advanced' && !isAcceleratePage && (
                        <i className="text-[6px] md:text-[9px]">
                          *Includes the Premium curriculum also
                        </i>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className="flex items-center justify-center">
              <DesktopCurriculum
                curriculumSection={currentCurriculum}
                className="hidden"
                isLightTheme={!dark}
                isSDPage={isSDPage}
              />
              <MobileCurriculum
                curriculumSection={currentCurriculum}
                curriculumSection3Ref={curriculumSection3Ref}
                secondCurriculum={secondCurriculum}
                optionCurriculum={optionCurriculum}
                curriculumSection2Ref={curriculumSection2Ref}
                isTypeQA={isTypeQA}
                className="md:hidden"
                isLightTheme={!dark}
              />
            </div>
          </div>
        )}
        {brochureViewHref && (
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <LeadGenerationButton
              text="View Curriculum"
              isOnePageForm
              formHeading="Program Curriculum"
              pageOneButtonText="View Now"
              buttonLocation={LEAD_GEN_DOWNLOAD_CURRICULUM}
              onSubmitPageOne={triggerCurriculumWebengageFlow}
              {...(state.email &&
                state.phone && {
                  onClick: () => {
                    triggerCurriculumWebengageFlow(
                      state.email,
                      state.phone,
                      EButtonType.DOWNLOAD_CURRICULUM_BUTTON,
                    );
                  },
                })}
            />
          </div>
        )}
      </div>
    </SectionLayout>
  );
});

export default CurriculumV4;
